import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError, apiFormData } from '@/lib/api';
import { clockOffsetMs, readClockOffset, rememberServerNow } from '@/lib/attempt-clock';
import { deriveSavePhase, saveStatusText } from '@/lib/attempt-connection';
import { appendOfflineLog, browserAttemptStore, readDeviceId, withProgressAck, type AttemptDraft } from '@/lib/attempt-offline-store';
import { clearAttemptFiles, putAttemptFile } from '@/lib/attempt-file-queue';
import { deliverAttemptJob } from '@/lib/attempt-sync-client';
import { createAttemptSyncQueue, jobsForDraft, type SyncJob } from '@/lib/attempt-sync-queue';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

interface AttemptSyncInput {
  userId?: string | null;
  attemptId?: string | null;
  progressUrl?: string | null;
  submitUrl?: string | null;
  fileUrl?: string | null;
  onSynced?: () => void;
}

function publish(message: unknown) {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel('lms-attempt-sync');
  channel.postMessage(message);
  channel.close();
}

async function withLock(userId: string, run: () => Promise<void>) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    await navigator.locks.request(`lms-attempt-sync:${userId}`, run);
    return;
  }
  await run();
}

export function useAttemptSync(input: AttemptSyncInput) {
  const { online } = useOnlineStatus();
  const [queued, setQueued] = useState(false);
  const [sending, setSending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [serverOffsetMs, setServerOffsetMs] = useState<number | null>(null);
  const [deadlineAt, setDeadlineAt] = useState<string | null>(null);
  const [localAnswers, setLocalAnswers] = useState<Record<string, unknown> | null>(null);
  const urlsRef = useRef(input);
  urlsRef.current = input;
  const offsetRef = useRef<number | null>(null);
  const flushRef = useRef<() => Promise<void>>(async () => {});
  const timingRef = useRef<{ deadlineAt: string | null; graceUntil: string | null }>({
    deadlineAt: null,
    graceUntil: null,
  });

  const queueRef = useRef<ReturnType<typeof createAttemptSyncQueue> | null>(null);
  if (!queueRef.current) {
    queueRef.current = createAttemptSyncQueue(async (job: SyncJob) => {
      const result = await deliverAttemptJob(job);
      if (result.status >= 200 && result.status < 300 && job.kind === 'progress' && result.body && typeof result.body === 'object') {
        const store = browserAttemptStore();
        const draft = store.load(job.userId, job.attemptId);
        if (draft) {
          const body = result.body as { acceptedSeq?: number | null; deadlineAt?: string | null; graceUntil?: string | null; serverNow?: string | null };
          const offset = body.serverNow ? clockOffsetMs(body.serverNow) : null;
          if (body.serverNow) rememberServerNow(body.serverNow);
          const next = withProgressAck(draft, body, new Date().toISOString(), offset);
          store.save(job.userId, job.attemptId, next);
          setDirty(next.dirty);
          setConfirmed(!next.dirty);
          setSavedAt(next.savedAt ?? null);
          if (next.serverOffsetMs != null) {
            offsetRef.current = next.serverOffsetMs;
            setServerOffsetMs(next.serverOffsetMs);
          }
          setDeadlineAt(next.deadlineAt);
        }
      }
      if ((result.status >= 200 && result.status < 300 && job.kind === 'submit') || result.status === 409) {
        const store = browserAttemptStore();
        const draft = store.load(job.userId, job.attemptId);
        if (draft && result.status !== 409) {
          store.save(job.userId, job.attemptId, {
            ...draft,
            offlineLog: appendOfflineLog(draft.offlineLog, { type: 'submit-ok', at: new Date().toISOString(), seq: draft.seq }),
          });
        }
        store.clear(job.userId, job.attemptId);
        await clearAttemptFiles(job.userId, job.attemptId);
        setQueued(false);
        setDirty(false);
        setConfirmed(true);
        setAccepted(result.status !== 409);
        publish({ type: 'submitted', userId: job.userId, attemptId: job.attemptId });
        urlsRef.current.onSynced?.();
      }
      return result.status;
    });
  }

  const flush = useCallback(async () => {
    const queue = queueRef.current;
    const userId = urlsRef.current.userId;
    if (!queue || !userId) return;
    await withLock(userId, async () => {
      const result = await queue.drain();
      setPending(queue.snapshot().length > 0);
      if (result.retryIn != null) {
        window.setTimeout(() => {
          void flushRef.current();
        }, result.retryIn);
      }
    });
  }, []);
  flushRef.current = flush;

  const rememberTiming = useCallback((nextDeadline: string | null, graceUntil: string | null) => {
    timingRef.current = { deadlineAt: nextDeadline, graceUntil };
    setDeadlineAt((current) => (current === nextDeadline ? current : nextDeadline));
    const { userId, attemptId } = urlsRef.current;
    if (!userId || !attemptId) return;
    const store = browserAttemptStore();
    const draft = store.load(userId, attemptId);
    if (!draft || (draft.deadlineAt === nextDeadline && draft.graceUntil === graceUntil)) return;
    store.save(userId, attemptId, { ...draft, deadlineAt: nextDeadline, graceUntil });
  }, []);

  const enqueueDraft = useCallback(() => {
    const { userId, attemptId } = urlsRef.current;
    if (!userId || !attemptId) return false;
    const draft = browserAttemptStore().load(userId, attemptId);
    if (!draft) return false;
    const jobs = jobsForDraft(userId, attemptId, draft);
    for (const job of jobs) queueRef.current?.enqueue(job);
    return jobs.length > 0;
  }, []);

  useEffect(() => {
    if (!input.userId || !input.attemptId) return undefined;
    const draft = browserAttemptStore().load(input.userId, input.attemptId);
    setLocalAnswers(draft?.answers ?? null);
    setDirty(Boolean(draft?.dirty));
    setConfirmed(Boolean(draft && !draft.dirty && draft.seq > 0));
    setSavedAt(draft?.savedAt ?? null);
    const storedOffset = draft?.serverOffsetMs ?? readClockOffset();
    offsetRef.current = storedOffset || null;
    setServerOffsetMs(storedOffset || null);
    setDeadlineAt(draft?.deadlineAt ?? null);
    if (draft?.pendingSubmit) setQueued(true);
    if (enqueueDraft()) void flush();
    return undefined;
  }, [input.userId, input.attemptId, flush, enqueueDraft]);

  const onlineRef = useRef(online);
  useEffect(() => {
    const previous = onlineRef.current;
    onlineRef.current = online;
    const { userId, attemptId } = urlsRef.current;
    if (userId && attemptId) {
      const store = browserAttemptStore();
      const draft = store.load(userId, attemptId);
      if (draft && previous !== online) {
        store.save(userId, attemptId, {
          ...draft,
          offlineLog: appendOfflineLog(draft.offlineLog, {
            type: online ? 'online' : 'offline',
            at: new Date().toISOString(),
            seq: draft.seq,
          }),
        });
      }
    }
    if (!previous && online) {
      if (enqueueDraft()) void flush().then(() => urlsRef.current.onSynced?.());
      else urlsRef.current.onSynced?.();
    }
  }, [online, enqueueDraft, flush]);

  useEffect(() => {
    const kick = () => {
      if (enqueueDraft()) void flush();
    };
    window.addEventListener('focus', kick);
    const timer = window.setInterval(kick, 10_000);
    return () => {
      window.removeEventListener('focus', kick);
      window.clearInterval(timer);
    };
  }, [enqueueDraft, flush]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel('lms-attempt-sync');
    channel.onmessage = (event: MessageEvent<{ type?: string; attemptId?: string }>) => {
      if (event.data?.type === 'submitted' && event.data.attemptId === input.attemptId) {
        setQueued(false);
        setAccepted(true);
        setLocalAnswers(null);
      }
    };
    return () => channel.close();
  }, [input.attemptId]);

  const blankDraft = useCallback((answers: Record<string, unknown>, pendingSubmit: boolean): AttemptDraft | null => {
    const { userId, attemptId, progressUrl, submitUrl, fileUrl } = urlsRef.current;
    if (!userId || !attemptId || !progressUrl || !submitUrl || !fileUrl) return null;
    const store = browserAttemptStore();
    const prev = store.load(userId, attemptId);
    const seq = (prev?.seq ?? 0) + 1;
    const now = new Date().toISOString();
    const draft: AttemptDraft = {
      answers,
      seq,
      deviceId: readDeviceId(localStorage),
      clientSubmissionId: prev?.clientSubmissionId ?? null,
      pendingSubmit,
      dirty: true,
      deadlineAt: prev?.deadlineAt ?? timingRef.current.deadlineAt,
      graceUntil: prev?.graceUntil ?? timingRef.current.graceUntil,
      queuedAt: prev?.queuedAt ?? null,
      serverOffsetMs: prev?.serverOffsetMs ?? offsetRef.current,
      savedAt: prev?.savedAt ?? null,
      offlineLog: appendOfflineLog(prev?.offlineLog, { type: 'save-queued', at: now, seq }),
      progressUrl,
      submitUrl,
      fileUrl,
      updatedAt: now,
    };
    store.save(userId, attemptId, draft);
    setLocalAnswers(answers);
    return draft;
  }, []);

  const persistAnswers = useCallback((answers: Record<string, unknown>) => {
    const { userId, attemptId } = urlsRef.current;
    const prev = userId && attemptId ? browserAttemptStore().load(userId, attemptId) : null;
    const draft = blankDraft(answers, Boolean(prev?.pendingSubmit));
    if (!draft || !userId || !attemptId) return;
    queueRef.current?.enqueue({
      kind: 'progress',
      userId,
      attemptId,
      seq: draft.seq,
      deviceId: draft.deviceId,
      answers,
    });
    setDirty(true);
    setPending(true);
    if (typeof navigator === 'undefined' || navigator.onLine) void flush();
  }, [blankDraft, flush]);

  const submit = useCallback(async (answers: Record<string, unknown>) => {
    const { userId, attemptId } = urlsRef.current;
    const draft = blankDraft(answers, true);
    if (!draft || !userId || !attemptId) return;
    if (!draft.clientSubmissionId) draft.clientSubmissionId = crypto.randomUUID();
    draft.pendingSubmit = true;
    const offlineNow = typeof navigator !== 'undefined' && navigator.onLine === false;
    const markedAt = new Date().toISOString();
    if (offlineNow) draft.queuedAt = draft.queuedAt ?? markedAt;
    draft.offlineLog = appendOfflineLog(draft.offlineLog, {
      type: 'submit-queued',
      at: draft.queuedAt ?? markedAt,
      seq: draft.seq,
    });
    browserAttemptStore().save(userId, attemptId, draft);
    queueRef.current?.enqueue({
      kind: 'submit',
      userId,
      attemptId,
      answers,
      clientSubmissionId: draft.clientSubmissionId,
    });
    if (offlineNow) {
      setQueued(true);
      void flush();
      return;
    }
    setSending(true);
    try {
      await flush();
      const stillQueued = queueRef.current?.snapshot().some((job) => job.kind === 'submit') ?? false;
      if (stillQueued) {
        setQueued(true);
        const current = browserAttemptStore().load(userId, attemptId);
        if (current && !current.queuedAt) {
          const queuedAt = new Date().toISOString();
          browserAttemptStore().save(userId, attemptId, { ...current, queuedAt });
        }
      }
    } finally {
      setSending(false);
    }
  }, [blankDraft, flush]);

  const uploadFile = useCallback(async (questionId: string, file: File) => {
    const { userId, attemptId } = urlsRef.current;
    if (!userId || !attemptId) throw new Error('No hay un intento activo');
    const idempotencyKey = crypto.randomUUID();
    const placeholder = {
      mediaFileId: `local:${idempotencyKey}`,
      name: file.name,
      mimeType: file.type,
      size: file.size,
      pending: true,
    };
    const queueFile = async () => {
      await putAttemptFile({
        idempotencyKey,
        userId,
        attemptId,
        questionId,
        name: file.name,
        type: file.type || 'application/octet-stream',
        blob: file,
      });
      queueRef.current?.enqueue({ kind: 'file', userId, attemptId, questionId, idempotencyKey });
      setPending(true);
      void flush();
    };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await queueFile();
      return placeholder;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      const draft = browserAttemptStore().load(userId, attemptId);
      const path = draft?.fileUrl
        ? `${draft.fileUrl}/${questionId}/file`
        : `${urlsRef.current.fileUrl}/${questionId}/file`;
      return await apiFormData(path, form, { headers: { 'Idempotency-Key': idempotencyKey } });
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.status !== 0) throw error;
      await queueFile();
      return placeholder;
    }
  }, [flush]);

  const noteServerNow = useCallback((serverNow: string | null | undefined) => {
    if (!serverNow) return;
    const offset = rememberServerNow(serverNow);
    offsetRef.current = offset;
    setServerOffsetMs(offset);
    const { userId, attemptId } = urlsRef.current;
    if (!userId || !attemptId) return;
    const store = browserAttemptStore();
    const draft = store.load(userId, attemptId);
    if (!draft || draft.serverOffsetMs === offset) return;
    store.save(userId, attemptId, { ...draft, serverOffsetMs: offset });
  }, []);

  const savePhase = deriveSavePhase({ online, dirty, confirmed });

  return {
    online,
    queued,
    sending,
    accepted,
    pending,
    dirty,
    savePhase,
    saveStatus: saveStatusText(savePhase, savedAt),
    serverOffsetMs,
    deadlineAt,
    localAnswers,
    rememberTiming,
    noteServerNow,
    persistAnswers,
    submit,
    uploadFile,
    flush,
  };
}
